import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FolderKanban,
  Activity,
  CheckCircle2,
  Wallet,
  Search,
  Filter,
  Download,
  Layers,
  Megaphone,
  Brain,
  Sparkles,
  Handshake,
  Calendar,
  ExternalLink,
  Users,
  Briefcase,
  Clock,
  Paperclip,
  FileText,
  Link2,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import { API_BASE_URL, DOWNLOAD_BASE_URL } from "../lib/api";
import {
  useAdminStore,
  type AdminProjectRow,
} from "../store/useAdminStore";
import ProjectSummaryModal from "../components/projects/ProjectSummaryModal";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const STATUSES = [
  "Not Started",
  "In Progress",
  "On Hold",
  "Completed",
  "Cancelled",
];

const TYPES = [
  "Website",
  "Graphic Design",
  "Video Animation",
  "Marketing",
  "Trial-based",
];

const statusStyle = (s: string) => {
  if (s === "Completed") return "bg-emerald-100 text-emerald-700 ring-emerald-200";
  if (s === "In Progress") return "bg-amber-100 text-amber-700 ring-amber-200";
  if (s === "On Hold") return "bg-orange-100 text-orange-700 ring-orange-200";
  if (s === "Cancelled") return "bg-rose-100 text-rose-700 ring-rose-200";
  return "bg-slate-100 text-slate-700 ring-slate-200";
};

const milestoneStatusStyle = (s: string) => {
  if (s === "Released") return "bg-emerald-100 text-emerald-700";
  if (s === "In Escrow") return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
};

// How a project originated — Invited / Direct / Bid-generated / Upwork — each with a distinct chip.
const sourceMeta = (p: { source?: string; sourceBid?: boolean; origin?: string }) => {
  if (p.source === "Invitation")
    return { label: "INVITED", cls: "bg-violet-100 text-violet-700", icon: Sparkles };
  if (p.source === "Direct")
    return { label: "DIRECT", cls: "bg-amber-100 text-amber-700", icon: Handshake };
  if (p.sourceBid || p.origin === "BID")
    return { label: "BID", cls: "bg-[#d8e2ff]/60 text-[#004397]", icon: Megaphone };
  return { label: "UPWORK", cls: "bg-[#86f2e4]/40 text-[#006f66]", icon: Brain };
};

const Kpi = ({
  label,
  value,
  sub,
  icon: Icon,
  tone = "blue",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tone?: "blue" | "emerald" | "amber" | "violet";
}) => {
  const tones = {
    blue: "from-blue-50 to-blue-100/40 text-blue-700",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700",
    amber: "from-amber-50 to-amber-100/40 text-amber-700",
    violet: "from-violet-50 to-violet-100/40 text-violet-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em]">
          {label}
        </span>
        <div className={`w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br ${tones[tone]}`}>
          <Icon size={20} />
        </div>
      </div>
      <p className="text-[32px] font-black leading-none tracking-tight text-slate-900">
        {value}
      </p>
      {sub && (
        <p className="text-[12px] text-slate-500 mt-2 font-medium">{sub}</p>
      )}
    </div>
  );
};

const AdminProjectsPage = () => {
  const { adminProjects, fetchAdminProjects } = useAdminStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get("client") || "";
  const clientName = searchParams.get("name") || "";
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [originFilter, setOriginFilter] = useState("");
  const [profileFilter, setProfileFilter] = useState("");
  const [bdFilter, setBdFilter] = useState("");
  const [drawer, setDrawer] = useState<AdminProjectRow | null>(null);

  // Deep-link filters (?profile from Account KPIs, ?bd from a project's BD pill).
  useEffect(() => {
    const profileParam = searchParams.get("profile");
    if (profileParam) setProfileFilter(profileParam);
    const bdParam = searchParams.get("bd");
    if (bdParam) setBdFilter(bdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Deep-link from an invoice (?open=<projectId>) → open that project's drawer.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && adminProjects?.projects) {
      const found = adminProjects.projects.find((p) => p._id === openId);
      if (found) {
        setDrawer(found);
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminProjects, searchParams]);

  useEffect(() => {
    fetchAdminProjects(clientId ? { clientId } : undefined);
  }, [fetchAdminProjects, clientId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const params: {
        bdId?: string;
        profileId?: string;
        clientId?: string;
        status?: string;
        type?: string;
        origin?: string;
        search?: string;
      } = {};
      if (clientId) params.clientId = clientId;
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (originFilter) params.origin = originFilter;
      if (profileFilter) params.profileId = profileFilter;
      if (bdFilter) params.bdId = bdFilter;
      fetchAdminProjects(params);
    }, 250);
    return () => clearTimeout(handler);
  }, [
    clientId,
    search,
    statusFilter,
    typeFilter,
    originFilter,
    profileFilter,
    bdFilter,
    fetchAdminProjects,
  ]);

  const clearClientFilter = () => {
    searchParams.delete("client");
    searchParams.delete("name");
    setSearchParams(searchParams);
  };

  const exportCSV = () => {
    const rows = adminProjects?.projects || [];
    if (!rows.length) return;
    const headers = ["Project", "Type", "Status", "BD", "Client", "Profile", "Total", "Released", "Net Profit", "Margin %", "P/L Status", "Updated"];
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((p) =>
      [
        p.name, p.projectType, p.projectStatus, p.bd?.name || "", p.client?.name || "", p.profile?.name || "",
        p.total, p.released, p.financials?.netProfit ?? "", p.financials?.marginPct ?? "", p.financials?.profitStatus ?? "",
        new Date(p.updatedAt).toISOString().slice(0, 10),
      ].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `projects-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxStatusRev = useMemo(
    () =>
      Math.max(1, ...(adminProjects?.byStatus.map((s) => s.revenue) || [0])),
    [adminProjects]
  );
  const maxTypeRev = useMemo(
    () => Math.max(1, ...(adminProjects?.byType.map((t) => t.revenue) || [0])),
    [adminProjects]
  );
  const maxProfileRev = useMemo(
    () => Math.max(1, ...(adminProjects?.byProfile.map((p) => p.revenue) || [0])),
    [adminProjects]
  );

  if (!adminProjects) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-[13px]">
            Loading projects...
          </div>
        </div>
      </AdminLayout>
    );
  }

  const t = adminProjects.totals;
  const completionRate = t.total ? Math.round((t.completed / t.total) * 100) : 0;
  const anyFilter =
    !!search ||
    !!statusFilter ||
    !!typeFilter ||
    !!originFilter ||
    !!profileFilter ||
    !!bdFilter;

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              All Projects
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Every project across all BDs · status, type, profile, BD pivots ·
              click any row for full milestone & cost detail
            </p>
          </div>
          <button
            onClick={exportCSV}
            disabled={!adminProjects?.projects?.length}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition disabled:opacity-50"
          >
            <Download size={15} />
            Export CSV
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            label="Total Projects"
            value={t.total.toLocaleString()}
            sub={`${completionRate}% completion rate`}
            icon={FolderKanban}
            tone="blue"
          />
          <Kpi
            label="In Progress"
            value={t.active.toLocaleString()}
            sub={`${t.onHold} on hold`}
            icon={Activity}
            tone="amber"
          />
          <Kpi
            label="Completed"
            value={t.completed.toLocaleString()}
            sub={`${fmtMoney(t.released)} released`}
            icon={CheckCircle2}
            tone="emerald"
          />
          <Kpi
            label="Total Revenue"
            value={fmtMoney(t.revenue)}
            sub={`${fmtMoney(t.escrow)} in escrow`}
            icon={Wallet}
            tone="violet"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PivotPanel
            title="By Status"
            items={adminProjects.byStatus.map((s) => ({
              label: s.status,
              count: s.count,
              revenue: s.revenue,
              style: statusStyle(s.status),
            }))}
            max={maxStatusRev}
          />
          <PivotPanel
            title="By Category"
            items={adminProjects.byType.map((t) => ({
              label: t.type,
              count: t.count,
              revenue: t.revenue,
              style: "bg-blue-50 text-blue-700 ring-blue-200",
            }))}
            max={maxTypeRev}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers size={18} className="text-[#0058c3]" />
              <h3 className="text-[15px] font-bold text-slate-900">
                By Upwork Profile
              </h3>
            </div>
            <div className="space-y-3">
              {adminProjects.byProfile.length === 0 ? (
                <p className="text-center text-slate-400 text-[12px] py-6">
                  No profile data yet.
                </p>
              ) : (
                adminProjects.byProfile.map((p) => {
                  const active = profileFilter === p.profileId;
                  return (
                    <button
                      key={p.profileId}
                      onClick={() =>
                        setProfileFilter(active ? "" : p.profileId)
                      }
                      className={`w-full text-left p-3 rounded-lg border transition ${
                        active
                          ? "border-blue-500 bg-blue-50/60"
                          : "border-transparent hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-bold text-slate-900">
                          {p.name}
                        </span>
                        <div className="text-right">
                          <span className="text-[13px] font-extrabold text-slate-900">
                            {fmtMoney(p.revenue)}
                          </span>
                          <span className="text-[10px] text-slate-500 ml-2">
                            {p.completed}/{p.count} done
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
                          style={{
                            width: `${(p.revenue / maxProfileRev) * 100}%`,
                          }}
                        />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-[#0058c3]" />
              <h3 className="text-[15px] font-bold text-slate-900">
                By Business Developer
              </h3>
            </div>
            <div className="space-y-2">
              {adminProjects.byBd.length === 0 ? (
                <p className="text-center text-slate-400 text-[12px] py-6">
                  No BD data yet.
                </p>
              ) : (
                adminProjects.byBd.slice(0, 8).map((bd) => {
                  const active = bdFilter === bd.bdId;
                  return (
                    <button
                      key={bd.bdId}
                      onClick={() => setBdFilter(active ? "" : bd.bdId)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition ${
                        active
                          ? "bg-blue-50/60 ring-1 ring-blue-200"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[11px] font-bold shrink-0">
                        {initials(bd.name)}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-[13px] font-semibold text-slate-900 truncate">
                          {bd.name}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {bd.count} projects · {bd.completed} completed
                        </p>
                      </div>
                      <span className="text-[13px] font-extrabold text-[#006a61]">
                        {fmtMoney(bd.revenue)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          {clientId && (
            <div className="px-5 pt-4 -mb-1">
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full pl-3 pr-2 py-1">
                Client: {clientName || "selected"}
                <button onClick={clearClientFilter} className="text-blue-500 hover:text-blue-800" title="Clear client filter">✕</button>
              </span>
            </div>
          )}
          <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <div className="flex-1 relative min-w-[260px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search projects by name..."
                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
            >
              <option value="">Status: All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
            >
              <option value="">Type: All</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
            >
              <option value="">Origin: All</option>
              <option value="BID">From Bid</option>
              <option value="ORGANIC">Organic</option>
            </select>
            {anyFilter && (
              <button
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setTypeFilter("");
                  setOriginFilter("");
                  setProfileFilter("");
                  setBdFilter("");
                }}
                className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <Th>Project</Th>
                  <Th>Contact</Th>
                  <Th>BD</Th>
                  <Th>Profile</Th>
                  <Th align="center">Status</Th>
                  <Th align="center">Origin</Th>
                  <Th>Completion</Th>
                  <Th align="right">Total</Th>
                  <Th align="right">Released</Th>
                  <Th align="right">Profit / Loss</Th>
                  <Th>Updated</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adminProjects.projects.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-6 py-14 text-center text-slate-400 text-[13px]"
                    >
                      No projects match the current filters.
                    </td>
                  </tr>
                ) : (
                  adminProjects.projects.map((p) => (
                    <tr
                      key={p._id}
                      onClick={() => setDrawer(p)}
                      className="hover:bg-slate-50/50 transition cursor-pointer"
                    >
                      <td className="px-4 py-4">
                        <p className="text-[13px] font-bold text-[#0070f3]">
                          {p.name}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded">
                            {p.projectType}
                          </span>
                          <span className="text-[9px] text-slate-400 font-bold uppercase">
                            {p.priceModel === "fixed" ? "FIXED" : "HOURLY"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-[12px] text-slate-700">
                          {p.client?.name || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {p.bd ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[9px] font-bold">
                              {initials(p.bd.name)}
                            </div>
                            <span className="text-[12px] text-slate-700">
                              {p.bd.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {p.profile ? (
                          <span className="text-[11px] text-slate-700 px-2 py-0.5 bg-slate-100 rounded font-semibold">
                            {p.profile.name}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-[11px]">
                            unassigned
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${statusStyle(
                            p.projectStatus
                          )}`}
                        >
                          {p.projectStatus}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center">
                          {(() => {
                            const sm = sourceMeta(p);
                            const Icon = sm.icon;
                            return (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${sm.cls}`}>
                                <Icon size={11} /> {sm.label}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 w-32">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className={`h-full ${
                                p.completionPct === 100
                                  ? "bg-emerald-500"
                                  : "bg-blue-500"
                              }`}
                              style={{ width: `${p.completionPct}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-slate-600 font-bold">
                            {p.completionPct}%
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {p.milestones.released}/{p.milestones.total} milestones
                        </p>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-[13px] font-bold text-slate-900 font-mono">
                          {fmtMoney(p.total)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-[13px] font-bold text-[#006a61] font-mono">
                          {fmtMoney(p.released)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        {p.financials ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span
                              className={`text-[13px] font-bold font-mono ${
                                p.financials.netProfit < 0 ? "text-rose-600" : "text-emerald-600"
                              }`}
                            >
                              {p.financials.netProfit < 0 ? "−" : ""}
                              {fmtMoney(Math.abs(p.financials.netProfit))}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ring-1 ${
                                p.financials.profitStatus === "Loss"
                                  ? "bg-rose-50 text-rose-700 ring-rose-200"
                                  : p.financials.profitStatus === "Below Target"
                                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                                  : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                              }`}
                            >
                              {p.financials.marginPct}% · {p.financials.profitStatus}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-[11px] text-slate-500">
                          {formatDate(p.updatedAt)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {adminProjects.projects.length > 0 && (
            <div className="bg-slate-50/50 border-t border-slate-200 px-5 py-3 text-[12px] text-slate-500">
              Showing {adminProjects.projects.length} project
              {adminProjects.projects.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      {drawer && <ProjectDrawer project={drawer} onClose={() => setDrawer(null)} />}
    </AdminLayout>
  );
};

const PivotPanel = ({
  title,
  items,
  max,
}: {
  title: string;
  items: { label: string; count: number; revenue: number; style: string }[];
  max: number;
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
    <h3 className="text-[15px] font-bold text-slate-900 mb-4">{title}</h3>
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.label}>
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${it.style}`}
            >
              {it.label}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-[12px] text-slate-500">
                {it.count} projects
              </span>
              <span className="text-[13px] font-extrabold text-slate-900">
                {fmtMoney(it.revenue)}
              </span>
            </div>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
              style={{ width: `${(it.revenue / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProjectDrawer = ({
  project,
  onClose,
}: {
  project: AdminProjectRow;
  onClose: () => void;
}) => {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const navigate = useNavigate();
  const laborCost = project.resourceCosts.reduce((s, rc) => {
    if (rc.type === "fixed") return s + rc.amount;
    if (rc.type === "hourly") return s + rc.amount * rc.hours;
    if (rc.type === "percentage") return s + (project.total * rc.amount) / 100;
    return s;
  }, 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col"
      >

        <div className="p-6 border-b border-slate-200 bg-gradient-to-br from-blue-50/60 to-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-1">
                {project.projectType} ·{" "}
                {project.priceModel === "fixed" ? "Fixed Price" : "Hourly"}
              </p>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-tight">
                {project.name}
              </h2>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${statusStyle(
                    project.projectStatus
                  )}`}
                >
                  {project.projectStatus}
                </span>
                {(() => {
                  const sm = sourceMeta(project);
                  const Icon = sm.icon;
                  return (
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${sm.cls}`}>
                      <Icon size={12} /> {sm.label}
                    </span>
                  );
                })()}
                {project.profile && (
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold">
                    {project.profile.name}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <button
            onClick={() => setSummaryOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:opacity-90"
          >
            <Wallet size={16} /> Financial Summary
          </button>
          {summaryOpen && (
            <ProjectSummaryModal
              projectId={project._id}
              projectName={project.name}
              onClose={() => setSummaryOpen(false)}
            />
          )}


          <div>
            <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
              Financial Snapshot
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <DrawerStat label="Total" value={fmtMoney(project.total)} tone="blue" />
              <DrawerStat label="Escrow" value={fmtMoney(project.escrow)} tone="amber" />
              <DrawerStat
                label="Released"
                value={fmtMoney(project.released)}
                tone="emerald"
              />
            </div>
            <div className="mt-3 p-3 rounded-lg bg-slate-50 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                Labor Cost
              </span>
              <span className="text-[14px] font-extrabold text-slate-900">
                {fmtMoney(laborCost)}
              </span>
            </div>
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
              Completion
            </h3>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${
                    project.completionPct === 100
                      ? "bg-emerald-500"
                      : "bg-blue-500"
                  }`}
                  style={{ width: `${project.completionPct}%` }}
                />
              </div>
              <span className="text-[15px] font-extrabold text-slate-900">
                {project.completionPct}%
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              {project.milestones.released}/{project.milestones.total} milestones
              released · {project.milestones.inEscrow} in escrow
            </p>
          </div>

          {project.milestones.items.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
                Milestones
              </h3>
              <div className="space-y-2">
                {project.milestones.items.map((m, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50/40"
                  >
                    <div className="w-8 h-8 grid place-items-center rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-600">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-900 truncate">
                        {m.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                        {m.dueDate && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={10} /> {formatDate(m.dueDate)}
                          </span>
                        )}
                        {m.releasedAt && (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 size={10} className="text-emerald-600" />
                            Released {formatDate(m.releasedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[13px] font-bold text-slate-900 font-mono">
                        {fmtMoney(m.amount)}
                      </p>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${milestoneStatusStyle(m.status)}`}
                      >
                        {m.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.resourceCosts.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
                Resource Costs
              </h3>
              <div className="space-y-1.5">
                {project.resourceCosts.map((rc, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2.5 rounded-md bg-slate-50 text-[12px]"
                  >
                    <span className="text-slate-700 font-medium">{rc.label}</span>
                    <span className="font-mono font-semibold text-slate-900">
                      {rc.type === "fixed"
                        ? `$${rc.amount}`
                        : rc.type === "hourly"
                        ? `$${rc.amount}/hr × ${rc.hours}h`
                        : `${rc.amount}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(project.links?.proposal || project.links?.wonBid || project.links?.upworkJob) && (
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
                Linked Records
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {project.links?.wonBid && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full"><Link2 size={12} /> Won Bid</span>
                )}
                {project.links?.proposal && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 text-slate-700 px-2 py-1 rounded-full"><Link2 size={12} /> Proposal</span>
                )}
                {project.links?.upworkJob && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full"><Link2 size={12} /> Upwork Job</span>
                )}
              </div>
            </div>
          )}

          {project.attachments && project.attachments.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3 inline-flex items-center gap-1">
                <Paperclip size={12} /> Documents ({project.attachments.length})
              </h3>
              <div className="space-y-1.5">
                {project.attachments.map((a) => (
                  <a
                    key={a._id}
                    href={
                      a.r2Key
                        ? `${API_BASE_URL}/project/${project._id}/attachments/${a._id}/download`
                        : /^https?:\/\//.test(a.url)
                        ? a.url
                        : `${DOWNLOAD_BASE_URL.replace(/\/$/, "")}/${a.url.replace(/^\//, "")}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between p-2.5 rounded-md bg-slate-50 hover:bg-slate-100 text-[12px] transition"
                  >
                    <span className="flex items-center gap-2 min-w-0 text-slate-800">
                      <FileText size={14} className="text-blue-600 shrink-0" />
                      <span className="truncate">{a.name}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-semibold uppercase text-slate-400">{a.category}</span>
                      <ExternalLink size={12} className="text-slate-400" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PeoplePill
              icon={Briefcase}
              label="Business Developer"
              name={project.bd?.name || "Unassigned"}
              sub={project.bd?.email || ""}
              onClick={project.bd ? () => navigate(`/admin/projects?bd=${project.bd!._id}`) : undefined}
            />
            <PeoplePill
              icon={Users}
              label="Contact"
              name={project.client?.name || "Not assigned"}
              sub={project.client?.email || ""}
              onClick={project.client ? () => navigate(`/admin/clients?open=${project.client!._id}`) : undefined}
            />
          </div>

          <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-4 text-[11px] text-slate-500">
            <div className="flex items-center gap-1.5">
              <Calendar size={12} />
              Created {formatDate(project.createdAt)}
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={12} />
              Updated {formatDate(project.updatedAt)}
            </div>
          </div>
        </div>

      </aside>
    </div>
  );
};

const DrawerStat = ({
  label,
  value,
  tone = "blue",
}: {
  label: string;
  value: string;
  tone?: "blue" | "amber" | "emerald";
}) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`p-3 rounded-lg text-center ${tones[tone]}`}>
      <p className="text-[15px] font-extrabold leading-none">{value}</p>
      <p className="text-[9px] uppercase tracking-wider mt-1 font-bold">
        {label}
      </p>
    </div>
  );
};

const PeoplePill = ({
  icon: Icon,
  label,
  name,
  sub,
  onClick,
}: {
  icon: any;
  label: string;
  name: string;
  sub: string;
  onClick?: () => void;
}) => {
  const clickable = Boolean(onClick);
  return (
    <div
      onClick={onClick}
      role={clickable ? "button" : undefined}
      className={`p-3 rounded-lg border transition ${
        clickable
          ? "bg-white border-blue-200 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer"
          : "bg-slate-50 border-slate-100"
      }`}
    >
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
        <Icon size={11} />
        {label}
        {clickable && <ExternalLink size={10} className="ml-auto text-blue-400" />}
      </p>
      <p className={`text-[13px] font-bold truncate ${clickable ? "text-blue-700" : "text-slate-900"}`}>{name}</p>
      {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
    </div>
  );
};

const Th = ({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <th
    className={`px-4 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
  >
    {children}
  </th>
);

export default AdminProjectsPage;
